/**
 * Sidebar — ported from oakallow-nextjs/components/Sidebar.tsx.
 *
 * Visual markup is byte-for-byte identical. Swaps:
 *   - next/link → react-router's Link (prop name: to, not href)
 *   - usePathname() → useLocation().pathname
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import TeamSwitcher from './TeamSwitcher';
import OrgSwitcher from './OrgSwitcher';
import {
  LayoutDashboard,
  Wrench,
  Building2,
  Server,
  Database,
  ShieldCheck,
  Activity,
  Key,
  CreditCard,
  Settings,
  Layers,
  BookOpen,
  FileCode,
  Plug,
  Rocket,
  Code2,
  LifeBuoy,
  BarChart3,
  Shield,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Users,
  Handshake,
  Bot,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { MeResponse } from '@oakallow/shared';
import ContactFeedbackModal from './ContactFeedbackModal';
import { useLockdownStatus } from '@/lib/use-lockdown-status';

const TRACK_ACCENT = '#d4a017';
// OakGuard accent — deep burgundy. Tints the "guard" half of the wordmark, the
// icon, and the PREVIEW badge, the way TRACK_ACCENT does for liveoak Track.
const OAKGUARD_ACCENT = '#9b2242';

// Pinned at the top with no section header — these are the "where am I"
// pages every user hits constantly.
const pinnedNav = [
  { label: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Quick Start', href: '/dashboard/quickstart', icon: Rocket },
];

// POLICY — the permission tree: orgs → tenants → resources → tools → rules.
const policyNav = [
  { label: 'Organizations', href: '/dashboard/orgs', icon: Building2 },
  { label: 'Tenants', href: '/dashboard/tenants', icon: Server },
  { label: 'Resources', href: '/dashboard/resources', icon: Database },
  { label: 'Tools', href: '/dashboard/tools', icon: Wrench },
  { label: 'OakGuard', href: '/dashboard/oakguard', icon: ShieldCheck, oakguard: true, preview: true },
  { label: 'Permission Rules', href: '/dashboard/rules', icon: Layers },
];

// RUNTIME — what's happening / what happened. Agent Skills lives here (not
// Policy) because it's an operational artifact every role uses — members
// download their org's skill here even though they can't see the admin
// Organizations page. Server-side, GET /api/orgs/:id/skill scopes by the
// caller's role on each org, so the member-safe Skills page only lists orgs
// they can act on.
const runtimeNav = [
  { label: 'Approvals', href: '/dashboard/approvals', icon: ShieldCheck, preview: false },
  { label: 'Activity', href: '/dashboard/activity', icon: Activity, preview: false },
  { label: 'Agent Skills', href: '/dashboard/skills', icon: FileCode, preview: false },
  { label: 'liveoak Track', href: '/dashboard/track', icon: BarChart3, preview: true },
];

const developerNav = [
  { label: 'API Builder', href: '/dashboard/builder', icon: Code2 },
  { label: 'API Docs', href: '/dashboard/quickstart-rest', icon: BookOpen },
  { label: 'MCP Connector', href: '/dashboard/quickstart-mcp', icon: Plug },
  { label: 'Agent Connector', href: '/dashboard/quickstart-agent', icon: Bot },
  { label: 'OpenAPI Spec', href: 'https://api.oakallow.io/docs', icon: FileCode, external: true },
];

const accountNav = [
  { label: 'Team', href: '/dashboard/team', icon: Users },
  { label: 'Agents', href: '/dashboard/agents', icon: Bot },
  { label: 'API Keys', href: '/dashboard/keys', icon: Key },
  { label: 'Billing', href: '/dashboard/billing', icon: CreditCard },
  { label: 'Security', href: '/dashboard/security', icon: Shield },
  // Enterprise SSO config — owner-only. Server-side, the /api/sso-admin/*
  // routes gate on team-owner via assertTeamOwner.
  { label: 'SSO', href: '/dashboard/sso', icon: ShieldCheck },
  // SCIM 2.0 provisioning — owner-only, same reason as SSO.
  { label: 'SCIM', href: '/dashboard/scim', icon: ShieldCheck },
  { label: 'Settings', href: '/dashboard/settings', icon: Settings },
  { label: 'Recipes', href: '/dashboard/recipes', icon: BookOpen },
  { label: 'Support', href: '/dashboard/support', icon: LifeBuoy },
];

export default function Sidebar({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { pathname } = useLocation();
  const [unreadSupport, setUnreadSupport] = useState(0);
  const [unreadApprovals, setUnreadApprovals] = useState(0);
  const [teamRole, setTeamRole] = useState<string>('owner');
  // OakGuard lockdown: a dot on the oakGuard link while anything is locked.
  const lockdownStatus = useLockdownStatus();
  // Signed-in user identity for the always-visible sidebar block.
  // Pulled from /api/me. Falling back to '' renders nothing.
  const [signedInName, setSignedInName] = useState<string>('');
  const [signedInEmail, setSignedInEmail] = useState<string>('');
  const [signedInAuthMethod, setSignedInAuthMethod] = useState<string>('passkey');
  // True when the user is viewing their own account (no team-context
  // impersonation). Default true so we don't briefly hide the Security
  // tile during the initial /api/me load on a fresh navigation.
  const [isSelf, setIsSelf] = useState<boolean>(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Verified-partner linkage from /api/me. If non-null the caller's email
  // matches a contact on some verified-partner authority — show the
  // Partner sidebar entry with a badge for in-progress + pending-review
  // authority counts.
  const [partnerLinked, setPartnerLinked] = useState<boolean>(false);
  const [partnerBadge, setPartnerBadge] = useState<number>(0);
  // Count of authorities this user's email has been granted "see + enable"
  // access to. Surfaces an "Authority access" entry even when not yet linked to
  // any partner, so the grantee can find Integrations / decline.
  const [partnerInviteCount, setPartnerInviteCount] = useState<number>(0);

  // Section collapse state — default-open if the user is currently on one of
  // that section's pages, so navigation feels oriented.
  const [policyOpen, setPolicyOpen] = useState(() =>
    policyNav.some((n) => pathname === n.href)
  );
  const [runtimeOpen, setRuntimeOpen] = useState(() =>
    runtimeNav.some((n) => pathname === n.href || pathname.startsWith(`${n.href}/`))
  );
  const [developerOpen, setDeveloperOpen] = useState(() =>
    developerNav.some((n) => pathname === n.href)
  );
  const [accountOpen, setAccountOpen] = useState(() =>
    accountNav.some((n) => pathname === n.href)
  );

  const isOwner = teamRole === 'owner';
  const canManageConfig = teamRole === 'owner' || teamRole === 'admin';
  const isMember = teamRole === 'member';

  // Members see a trimmed sidebar: Overview + Runtime + a stripped Account
  // section. Hiding Policy and Developer entirely (rather than rendering
  // empty sections) keeps the navigation honest about what they can act on.
  // Admins and owners keep the full sidebar.
  const visiblePinnedNav = isMember
    ? pinnedNav.filter((n) => n.label === 'Overview')
    : pinnedNav;

  // Policy / Developer sections are config-author surfaces — hide them
  // wholesale for members so they're not confronted with read-only stubs.
  // Admin/owner filter logic stays as-is.
  const visiblePolicyNav = isMember
    ? []
    : policyNav.filter((item) => {
        if (item.label === 'Organizations') return canManageConfig;
        if (item.label === 'Permission Rules') return canManageConfig;
        if (item.label === 'OakGuard') return canManageConfig;
        return true; // Tools, Tenants, Resources, Integrations
      });

  const visibleDeveloperNav = isMember
    ? []
    : developerNav.filter((item) => {
        if (item.label === 'API Builder') return canManageConfig;
        return true; // API Docs / MCP / OpenAPI Spec — anyone admin+
      });

  const visibleAccountNav = accountNav.filter((item) => {
    // Members see only Team (their colleagues), Security (their own
    // passkeys/recovery codes), and Support (account help). Everything
    // else is admin- or owner-managed.
    if (isMember) {
      if (item.label === 'Team') return true;
      if (item.label === 'Security') return isSelf;
      if (item.label === 'Support') return true;
      return false;
    }
    if (item.label === 'Agents') return canManageConfig;
    if (item.label === 'API Keys') return canManageConfig;
    if (item.label === 'Billing') return isOwner;
    if (item.label === 'Settings') return canManageConfig;
    // Security state (passkeys, recovery codes) is private per-account.
    // Hide entirely when the user has switched team-context to view
    // another team member — they should see no trace of the section.
    if (item.label === 'Security') return isSelf;
    // SSO + SCIM are owner-only: configuring how the company signs in
    // belongs to the team owner. Server-side gates match (assertTeamOwner
    // on /api/sso-admin/*, requireOwnerRole on /api/scim-admin/*).
    if (item.label === 'SSO') return isOwner;
    if (item.label === 'SCIM') return isOwner;
    return true; // Recipes
  });

  useEffect(() => {
    function fetchCounts() {
      // These routes aren't ported in Phase 1, so the fetches will 404 in
      // dev. Swallowing errors keeps the sidebar functional — badges just
      // stay at 0 until /api/support/notifications + /api/approvals are
      // ported in a later phase.
      // TODO (Phase 2+): remove the try/catch suppression once the routes land.
      fetch('/api/support/notifications?limit=1', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) setUnreadSupport(data.unreadCount || 0);
        })
        .catch(() => {});
      fetch('/api/approvals?filter=all', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) {
            // Badge = unread AND still actionable. Expired / approved /
            // denied requests are terminal — they shouldn't inflate the
            // "needs your attention" indicator even if the user never
            // tapped them while they were pending.
            const count = (data.approvals || []).filter(
              (a: { read_at?: string | null; status?: string }) =>
                !a.read_at && a.status === 'pending'
            ).length;
            setUnreadApprovals(count);
          }
        })
        .catch(() => {});
    }
    fetchCounts();

    // Pull team_role + is_self from /api/me (this IS ported in Phase 1).
    api<MeResponse>('/api/me')
      .then((data) => {
        if (data?.team_role) setTeamRole(data.team_role);
        if (data?.developer?.name) setSignedInName(data.developer.name);
        if (data?.developer?.email) setSignedInEmail(data.developer.email);
        if (data?.developer?.auth_method) setSignedInAuthMethod(data.developer.auth_method);
        // is_self is optional on older server builds; treat absent as true
        // so we don't break clients deployed before the server change.
        setIsSelf(data?.is_self !== false);
        // Partner linkage drives the conditional "Partner" sidebar item.
        const p = (data as unknown as { partner?: { id: string; sidebar_badge?: number } | null })?.partner;
        if (p?.id) {
          setPartnerLinked(true);
          setPartnerBadge(p.sidebar_badge || 0);
        } else {
          setPartnerLinked(false);
          setPartnerBadge(0);
        }
        const grants = (data as unknown as { authority_grants?: unknown[] })?.authority_grants;
        setPartnerInviteCount(Array.isArray(grants) ? grants.length : 0);
      })
      .catch(() => {});

    const interval = setInterval(fetchCounts, 30000);
    const handleRead = () => setUnreadSupport(0);
    const handleApprovalsRead = (event: Event) => {
      const detail = (event as CustomEvent<{ unreadCount?: number }>).detail;
      if (typeof detail?.unreadCount === 'number') setUnreadApprovals(detail.unreadCount);
      else fetchCounts();
    };
    window.addEventListener('support-notifications-read', handleRead);
    window.addEventListener('approvals-read-updated', handleApprovalsRead);
    return () => {
      clearInterval(interval);
      window.removeEventListener('support-notifications-read', handleRead);
      window.removeEventListener('approvals-read-updated', handleApprovalsRead);
    };
  }, []);

  function SectionTitle({
    label,
    collapsible,
    open,
    onToggle,
  }: {
    label: string;
    collapsible: boolean;
    open: boolean;
    onToggle?: () => void;
  }) {
    if (!collapsible) {
      return <div className="sidebar-section-title">{label}</div>;
    }
    return (
      <button
        onClick={onToggle}
        className="sidebar-section-title"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.25rem 0',
          color: 'inherit',
          font: 'inherit',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          fontWeight: 600,
        }}
      >
        {label}
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
    );
  }

  return (
    <aside style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div
        className="sidebar-logo"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img
            src="/oakallow-icon.png"
            alt=""
            style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)' }}
          />
          {!collapsed && (
            <span style={{ letterSpacing: '-0.02em' }}>
              oak<span style={{ color: 'var(--brand-primary)' }}>allow</span>
            </span>
          )}
        </div>
        {!collapsed && (
          <button
            className="sidebar-collapse-btn"
            onClick={() => onToggleCollapse()}
            title="Collapse sidebar"
          >
            <ChevronsLeft size={14} />
          </button>
        )}
      </div>
      {collapsed && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0' }}>
          <button
            className="sidebar-collapse-btn"
            onClick={() => onToggleCollapse()}
            title="Expand sidebar"
          >
            <ChevronsRight size={14} />
          </button>
        </div>
      )}

      {/* Signed-in user dropdown moved to the header (top-right, next to
          the theme switcher) so the team switcher can sit at the top of
          the sidebar where it belongs. */}

      <TeamSwitcher collapsed={collapsed} />
      <OrgSwitcher collapsed={collapsed} />

      <nav className="sidebar-nav">
        {/* Pinned at the top — no section header. */}
        <div className="sidebar-section">
          {visiblePinnedNav.map(({ label, href, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              className={`sidebar-link ${pathname === href ? 'active' : ''}`}
              title={collapsed ? label : undefined}
            >
              <Icon />
              {!collapsed && label}
            </Link>
          ))}
          {/* Partner — visible only when the signed-in user is linked to
              a verified-partner record (applicant_user_id match). Badge
              shows in-progress + pending-review authority count. Hidden
              for members since the partner record is team-level and they
              cannot act on it. */}
          {partnerLinked && !isMember && (
            <Link
              to="/dashboard/partner"
              className={`sidebar-link ${pathname === '/dashboard/partner' ? 'active' : ''}`}
              title={collapsed ? 'Partner' : undefined}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Handshake />
                {!collapsed && 'Partner'}
              </span>
              {partnerBadge > 0 && !collapsed && (
                <span style={{
                  background: 'var(--brand-primary)', color: 'white',
                  fontSize: '0.7rem', fontWeight: 700,
                  padding: '0.05rem 0.4rem', borderRadius: '999px',
                  lineHeight: 1.2,
                }}>{partnerBadge}</span>
              )}
            </Link>
          )}
          {/* Authority access — the user isn't a partner themselves but has
              been granted "see + enable" access to one or more authorities.
              Route them to the Partner page (which lists granted authorities +
              links to Integrations) with a count badge. Takes priority over
              "Become a partner". */}
          {!partnerLinked && !isMember && partnerInviteCount > 0 && (
            <Link
              to="/dashboard/partner"
              className={`sidebar-link ${pathname === '/dashboard/partner' ? 'active' : ''}`}
              title={collapsed ? 'Authority access' : undefined}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Handshake />
                {!collapsed && 'Authority access'}
              </span>
              {!collapsed && (
                <span style={{
                  background: 'var(--brand-primary)', color: 'white',
                  fontSize: '0.7rem', fontWeight: 700,
                  padding: '0.05rem 0.4rem', borderRadius: '999px', lineHeight: 1.2,
                }}>{partnerInviteCount}</span>
              )}
            </Link>
          )}
          {/* Become a partner — visible only to users not yet linked to
              a partner record AND with no pending invite. Hidden once they
              apply, and hidden for members (applying represents the team's
              corporate identity, which is an owner-level decision). */}
          {!partnerLinked && !isMember && partnerInviteCount === 0 && (
            <Link
              to="/partners/apply"
              className={`sidebar-link ${pathname === '/partners/apply' ? 'active' : ''}`}
              title={collapsed ? 'Become a partner' : undefined}
            >
              <Handshake />
              {!collapsed && 'Become a partner'}
            </Link>
          )}
        </div>

        {/* POLICY — the permission tree. Hidden entirely for members. */}
        {visiblePolicyNav.length > 0 && (
        <div className="sidebar-section">
          {!collapsed && (
            <SectionTitle
              label="Policy"
              collapsible
              open={policyOpen}
              onToggle={() => setPolicyOpen(!policyOpen)}
            />
          )}
          {(collapsed || policyOpen) &&
            visiblePolicyNav.map((item) => {
              const { label, href, icon: Icon } = item;
              const oakguard = (item as { oakguard?: boolean }).oakguard;
              const preview = (item as { preview?: boolean }).preview;
              return (
                <Link
                  key={href}
                  to={href}
                  className={`sidebar-link ${pathname === href ? 'active' : ''}`}
                  title={collapsed ? label : undefined}
                >
                  <Icon style={oakguard ? { color: OAKGUARD_ACCENT } : undefined} />
                  {!collapsed && (
                    oakguard ? (
                      // Wordmark stays neutral (the red "Guard" read too loud
                      // in the nav). The burgundy accent lives on the icon and
                      // the PREVIEW badge only.
                      <span style={{ whiteSpace: 'nowrap' }}>oakGuard</span>
                    ) : (
                      label
                    )
                  )}
                  {oakguard && lockdownStatus?.active && (
                    <span
                      role="img"
                      aria-label="Lockdown active"
                      title="Lockdown active"
                      style={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--danger, #dc2626)',
                        boxShadow: '0 0 0 2px var(--bg-primary)',
                        marginLeft: collapsed ? -10 : 0,
                        marginTop: collapsed ? -14 : 0,
                      }}
                    />
                  )}
                  {!collapsed && preview && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        padding: '0.1rem 0.35rem',
                        borderRadius: 'var(--radius-sm)',
                        color: OAKGUARD_ACCENT,
                        border: `1px solid ${OAKGUARD_ACCENT}`,
                        background: 'rgba(155, 34, 66, 0.08)',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                      }}
                    >
                      Preview
                    </span>
                  )}
                </Link>
              );
            })}
        </div>
        )}

        {/* RUNTIME — approvals + activity + LiveOak Track */}
        <div className="sidebar-section">
          {!collapsed && (
            <SectionTitle
              label="Runtime"
              collapsible
              open={runtimeOpen}
              onToggle={() => setRuntimeOpen(!runtimeOpen)}
            />
          )}
          {(collapsed || runtimeOpen) &&
            runtimeNav.map(({ label, href, icon: Icon, preview }) => {
              const isActive =
                pathname === href || (href !== '/dashboard' && pathname.startsWith(`${href}/`));
              return (
                <Link
                  key={href}
                  to={href}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  style={{ position: 'relative' }}
                  title={collapsed ? label : undefined}
                >
                  <Icon style={preview ? { color: TRACK_ACCENT } : undefined} />
                  {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{label}</span>}
                  {!collapsed && label === 'Approvals' && unreadApprovals > 0 && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        minWidth: 18,
                        height: 18,
                        padding: '0 5px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '999px',
                        background: '#f59e0b',
                        color: 'white',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {unreadApprovals > 9 ? '9+' : unreadApprovals}
                    </span>
                  )}
                  {!collapsed && preview && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        padding: '0.1rem 0.35rem',
                        borderRadius: 'var(--radius-sm)',
                        color: TRACK_ACCENT,
                        border: `1px solid ${TRACK_ACCENT}`,
                        background: 'rgba(212, 160, 23, 0.08)',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                      }}
                    >
                      Preview
                    </span>
                  )}
                </Link>
              );
            })}
        </div>

        {/* DEVELOPER — API builder + docs. Hidden entirely for members. */}
        {visibleDeveloperNav.length > 0 && (
        <div className="sidebar-section">
          {!collapsed && (
            <SectionTitle
              label="Developer"
              collapsible
              open={developerOpen}
              onToggle={() => setDeveloperOpen(!developerOpen)}
            />
          )}
          {(collapsed || developerOpen) &&
            visibleDeveloperNav.map((item) => {
              const { label, href, icon: Icon } = item;
              const external = href.startsWith('http://') || href.startsWith('https://');
              if (external) {
                return (
                  <a
                    key={href}
                    href={href}
                    className="sidebar-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    title={collapsed ? label : undefined}
                  >
                    <Icon />
                    {!collapsed && label}
                  </a>
                );
              }
              return (
                <Link
                  key={href}
                  to={href}
                  className={`sidebar-link ${pathname === href ? 'active' : ''}`}
                  title={collapsed ? label : undefined}
                >
                  <Icon />
                  {!collapsed && label}
                </Link>
              );
            })}
        </div>
        )}

        {/* ACCOUNT — team + keys + billing + settings + support */}
        <div className="sidebar-section">
          {!collapsed && (
            <SectionTitle
              label="Account"
              collapsible
              open={accountOpen}
              onToggle={() => setAccountOpen(!accountOpen)}
            />
          )}
          {(collapsed || accountOpen) &&
            visibleAccountNav.map(({ label, href, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                className={`sidebar-link ${pathname === href ? 'active' : ''}`}
                style={{ position: 'relative' }}
                title={collapsed ? label : undefined}
              >
                <Icon />
                {!collapsed && label}
                {!collapsed && label === 'Support' && unreadSupport > 0 && (
                  <span
                    aria-label={`${unreadSupport} unread support notifications`}
                    style={{
                      marginLeft: 'auto',
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '999px',
                      background: 'var(--brand-primary)',
                      color: 'white',
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {unreadSupport > 9 ? '9+' : unreadSupport}
                  </span>
                )}
              </Link>
            ))}
        </div>
      </nav>

      {/* Legal footer */}
      <div
        className="sidebar-footer"
        style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid var(--border-color)',
          display: collapsed ? 'none' : 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem 0.75rem',
          justifyContent: 'center',
        }}
      >
        {[
          { label: 'About', href: '/about' },
          { label: 'Info', href: '/info' },
          { label: 'Privacy', href: '/privacy' },
          { label: 'Terms', href: '/terms' },
          { label: 'Acceptable Use', href: '/acceptable-use' },
        ].map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.625rem',
              color: 'var(--text-tertiary)',
              textDecoration: 'none',
            }}
          >
            {link.label}
          </a>
        ))}
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            fontSize: '0.625rem',
            color: 'var(--text-tertiary)',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          Contact
        </button>
      </div>
      <ContactFeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        context="dashboard"
      />
    </aside>
  );
}
